import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  LinearProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  School as SchoolIcon,
  EmojiEvents as TrophyIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LessonRenderer from './LessonRenderer';
import QuizEngine from './QuizEngine';
import CertificateDialog from './CertificateDialog';
import trainingService, { TrainingLesson, TrainingProgress } from '../../services/trainingService';

interface TrainingShellProps {
  moduleId: string;
  moduleName: string;
  backUrl?: string;
}

const TrainingShell: React.FC<TrainingShellProps> = ({ moduleId, moduleName, backUrl = '/learn' }) => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<TrainingLesson[]>([]);
  const [progress, setProgress] = useState<TrainingProgress | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [moduleQuizQuestions, setModuleQuizQuestions] = useState<any[]>([]);
  const [userName, setUserName] = useState<string>('');

  // Load lessons and progress
  useEffect(() => {
    loadTrainingData();
    loadUserName();
  }, [moduleId]);
  
  // Load user name from localStorage or API
  const loadUserName = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserName(user.name || '');
      }
    } catch (err) {
      console.error('Error loading user name:', err);
    }
  };

  // Keyboard shortcut: Shift + Cmd/Ctrl + E to jump to final exam
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Debug logging
      if (event.shiftKey && (event.metaKey || event.ctrlKey)) {
        console.log('Key combo detected:', {
          key: event.key,
          code: event.code,
          shiftKey: event.shiftKey,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey
        });
      }
      
      // Check for Shift + (Cmd on Mac / Ctrl on Windows) + E
      const modifierKey = event.metaKey || event.ctrlKey;
      const isEKey = event.key === 'e' || event.key === 'E';
      
      if (event.shiftKey && modifierKey && isEKey) {
        event.preventDefault();
        event.stopPropagation();
        console.log('🎯 Jumping to final exam! Total lessons:', lessons.length);
        // Jump to final quiz (last step)
        setActiveStep(lessons.length);
      }
    };

    console.log('📌 Keyboard shortcut listener attached. Lessons count:', lessons.length);
    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      console.log('🔌 Keyboard shortcut listener removed');
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [lessons.length, setActiveStep]);

  const loadTrainingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [lessonsData, progressData, moduleData] = await Promise.all([
        trainingService.getModuleLessons(moduleId),
        trainingService.getModuleProgress(moduleId, moduleName).catch(() => null),
        trainingService.getModuleBySlug(moduleId).catch(() => null)
      ]);

      setLessons(lessonsData);
      
      // Extract quiz questions from module if available
      if (moduleData?.quizzes && moduleData.quizzes.length > 0) {
        const quizQuestions = moduleData.quizzes[0].questions || [];
        setModuleQuizQuestions(quizQuestions);
      }
      
      // Set progress if available (may be null if not authenticated)
      if (progressData) {
        setProgress(progressData);
        
        // Set active step based on progress
        if (progressData.status === 'completed') {
          setActiveStep(lessonsData.length); // Show final quiz/certificate
        } else if (progressData.lessons.length > 0) {
          const lastCompletedIndex = progressData.lessons.findIndex((l: any) => !l.completed);
          setActiveStep(lastCompletedIndex === -1 ? lessonsData.length : lastCompletedIndex);
        }
      }
    } catch (err: any) {
      console.error('Error loading training data:', err);
      setError(err.response?.data?.message || 'Failed to load training content');
    } finally {
      setLoading(false);
    }
  };

  const handleLessonComplete = async () => {
    if (!lessons[activeStep]) return;

    try {
      const updatedProgress = await trainingService.completeLesson(
        lessons[activeStep]._id,
        moduleId
      );
      setProgress(updatedProgress);

      // Move to next lesson or quiz
      if (activeStep < lessons.length - 1) {
        setActiveStep(activeStep + 1);
      } else {
        setActiveStep(lessons.length); // Move to final quiz
      }
    } catch (err: any) {
      console.error('Error completing lesson:', err);
      setError(err.response?.data?.message || 'Failed to save progress');
    }
  };

  const handleQuizComplete = async (score: number, passed: boolean) => {
    try {
      const updatedProgress = await trainingService.submitFinalQuiz(moduleId, score, passed);
      setProgress(updatedProgress);

      if (passed) {
        setShowCertificate(true);
      }
    } catch (err: any) {
      console.error('Error submitting quiz:', err);
      setError(err.response?.data?.message || 'Failed to submit quiz');
    }
  };

  const handleIssueCertificate = async (recipientName: string) => {
    try {
      await trainingService.issueCertificate(moduleId, recipientName);
      await loadTrainingData(); // Reload to get updated certificate info
    } catch (err: any) {
      console.error('Error issuing certificate:', err);
      setError(err.response?.data?.message || 'Failed to issue certificate');
    }
  };

  const handleRetakeTraining = async () => {
    if (window.confirm('Are you sure you want to retake this training? This will reset all your progress and allow you to improve your score.')) {
      try {
        await trainingService.resetModuleProgress(moduleId);
        await loadTrainingData(); // Reload to reset state
        setActiveStep(0); // Start from beginning
      } catch (err: any) {
        console.error('Error resetting progress:', err);
        setError(err.response?.data?.message || 'Failed to reset progress');
      }
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleNext = () => {
    if (activeStep < lessons.length) {
      setActiveStep(activeStep + 1);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ mt: 2, color: '#71767A' }}>
          Loading training content...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate(backUrl)}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const currentLesson = lessons[activeStep];
  const isQuizStep = activeStep === lessons.length;
  const progressPercentage = progress?.completionPercentage || 0;

  // Collect all quiz questions from all lessons AND module quizzes
  const lessonQuizQuestions = lessons.flatMap(lesson => lesson.quiz || []);
  const allQuizQuestions = lessonQuizQuestions.length > 0 ? lessonQuizQuestions : moduleQuizQuestions;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(backUrl)}
          sx={{ mb: 2, color: 'primary.main' }}
        >
          Back to Learn
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {moduleName}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'divider',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main'
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2" sx={{ color: '#71767A' }}>
            {progressPercentage}% Complete
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                console.log('Skip button clicked, jumping to final exam');
                setActiveStep(lessons.length);
              }}
              sx={{
                fontSize: '11px',
                padding: '2px 8px',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Skip to Final Exam
            </Button>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#71767A', 
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              or press <kbd style={{ 
                padding: '2px 6px', 
                border: '1px solid #ccc', 
                borderRadius: '3px',
                backgroundColor: '#f5f5f5',
                fontFamily: 'monospace',
                fontSize: '11px'
              }}>Shift + {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} + E</kbd>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {lessons.map((lesson, index) => (
            <Step key={lesson._id} completed={progress?.lessons.some(l => l.lessonId === lesson._id && l.completed)}>
              <StepLabel>{lesson.title}</StepLabel>
            </Step>
          ))}
          <Step completed={progress?.finalQuizPassed}>
            <StepLabel>Final Quiz</StepLabel>
          </Step>
        </Stepper>
      </Paper>

      {/* Content */}
      <Paper sx={{ p: 4, mb: 4, minHeight: 400 }}>
        {isQuizStep ? (
          <>
            {progress?.finalQuizPassed ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <TrophyIcon sx={{ fontSize: 80, color: '#FFD700', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ color: 'primary.main' }}>
                  Congratulations!
                </Typography>
                <Typography variant="h6" paragraph sx={{ color: '#71767A' }}>
                  You have completed the {moduleName} training
                </Typography>
                <Typography variant="body1" paragraph>
                  Final Score: {progress.finalQuizScore}%
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setShowCertificate(true)}
                    sx={{
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }}
                  >
                    {progress.certificateIssued ? 'View Certificate' : 'Get Certificate'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<RefreshIcon />}
                    onClick={handleRetakeTraining}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  >
                    Retake to Improve Score
                  </Button>
                </Box>
              </Box>
            ) : (
              <QuizEngine
                questions={allQuizQuestions}
                onComplete={handleQuizComplete}
                passingScore={progress?.passingScore || 70}
                title="Final Assessment"
              />
            )}
          </>
        ) : currentLesson ? (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" sx={{ color: '#71767A' }}>
                Lesson {activeStep + 1} of {lessons.length}
              </Typography>
              <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
                {currentLesson.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#71767A' }}>
                Duration: {currentLesson.duration}
              </Typography>
            </Box>

            <LessonRenderer
              contentSections={currentLesson.contentSections}
              keyTakeaways={currentLesson.keyTakeaways}
            />
          </>
        ) : null}
      </Paper>

      {/* Navigation */}
      {!isQuizStep && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handlePrevious}
            disabled={activeStep === 0}
            sx={{ color: 'primary.main' }}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={activeStep === lessons.length - 1 ? handleLessonComplete : handleNext}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            {activeStep === lessons.length - 1 ? 'Complete & Take Quiz' : 'Next Lesson'}
          </Button>
        </Box>
      )}

      {/* Certificate Dialog */}
      <CertificateDialog
        open={showCertificate}
        onClose={() => setShowCertificate(false)}
        onIssue={handleIssueCertificate}
        moduleName={moduleName}
        certificateId={progress?.certificateId}
        issuedDate={progress?.certificateIssuedAt}
        userName={userName}
      />
    </Container>
  );
};

export default TrainingShell;
